/**
 * Dataset do financeiro gerencial (Task 16, brief 3.8).
 *
 * Uma fonte (`/api/platform/financial/summary`) com o seu estado declarado, no
 * mesmo vocabulário das demais telas (`live` vs `awaiting_contract`): fonte
 * ausente é dita, nunca convertida em "tudo conciliado" ou "margem zero".
 *
 * Os seletores aqui são a fonte ÚNICA de "quais registros compõem este
 * recorte": conciliação, margens e rentabilidade leem daqui, então não existe
 * contagem de tela discordando de outra. Puro e sem I/O.
 */
import type { DashboardSourceState } from "../dashboard/kpis.js";
import type {
  FinancialReconciliation,
  FinancialSummaryResponse,
  ManagerialAccount,
  ManagerialEntry,
  OrderMargin,
  ProductMargin
} from "../api/types.js";
import { MANAGERIAL_MIN_MARGIN_PCT } from "./managerial.js";

export interface FinancialSources {
  financial: DashboardSourceState;
}

export interface FinancialDataset {
  summary: FinancialSummaryResponse["summary"];
  reconciliations: FinancialReconciliation[];
  orderMargins: OrderMargin[];
  productMargins: ProductMargin[];
  entries: ManagerialEntry[];
  accounts: ManagerialAccount[];
  sources: FinancialSources;
}

/** Resumo vazio honesto: nada buscado ainda (não é "tudo conciliado"). */
export function emptyFinancialSummary(): FinancialSummaryResponse["summary"] {
  return {
    reconciled: 0,
    diverged: 0,
    pending: 0,
    revenueCents: 0,
    paymentFeeCents: 0,
    commissionCents: 0,
    costCents: 0,
    marginCents: 0,
    marginPct: null,
    belowFloorOrders: 0,
    minMarginPct: MANAGERIAL_MIN_MARGIN_PCT,
    source: "simulator"
  };
}

/** Estado inicial honesto: fonte ainda não respondeu (não é "sem dado"). */
export function emptyFinancialDataset(): FinancialDataset {
  return {
    summary: emptyFinancialSummary(),
    reconciliations: [],
    orderMargins: [],
    productMargins: [],
    entries: [],
    accounts: [],
    sources: { financial: "awaiting_contract" }
  };
}

export function isFinancialSourceAwaitingContract(dataset: FinancialDataset): boolean {
  return dataset.sources.financial === "awaiting_contract";
}

/** A fonte declarou que os números vêm do simulador (credencial ausente)? */
export function isFinancialFromSimulator(dataset: FinancialDataset): boolean {
  return dataset.summary.source === "simulator";
}

// ---------------------------------------------------------------------------
// Conciliação
// ---------------------------------------------------------------------------

export function reconciliationById(dataset: FinancialDataset, id: string): FinancialReconciliation | null {
  return dataset.reconciliations.find((row) => row.id === id) ?? null;
}

/** Só as conciliações que DIVERGEM — a fila que o Financeiro precisa resolver. */
export function divergedReconciliations(dataset: FinancialDataset): FinancialReconciliation[] {
  return dataset.reconciliations.filter((row) => row.status === "diverged");
}

// ---------------------------------------------------------------------------
// Margens
// ---------------------------------------------------------------------------

export function orderMarginById(dataset: FinancialDataset, platformOrderId: string): OrderMargin | null {
  return dataset.orderMargins.find((row) => row.platformOrderId === platformOrderId) ?? null;
}

export function productMarginById(dataset: FinancialDataset, productId: string): ProductMargin | null {
  return dataset.productMargins.find((row) => row.productId === productId) ?? null;
}

/** Pedidos com margem realizada abaixo do piso aprovado (alerta de gestão). */
export function ordersBelowFloor(dataset: FinancialDataset): OrderMargin[] {
  return dataset.orderMargins.filter((row) => row.belowFloor);
}

// ---------------------------------------------------------------------------
// Lançamentos gerenciais (drill-down de rentabilidade)
// ---------------------------------------------------------------------------

/** Lançamentos de um pedido (o que a rentabilidade do pedido soma). */
export function entriesForOrder(dataset: FinancialDataset, platformOrderId: string): ManagerialEntry[] {
  return dataset.entries.filter((entry) => entry.platformOrderId === platformOrderId);
}

/** Lançamentos de um produto. */
export function entriesForProduct(dataset: FinancialDataset, productId: string): ManagerialEntry[] {
  return dataset.entries.filter((entry) => entry.productId === productId);
}

/** Lançamentos gerados por uma conciliação. */
export function entriesForReconciliation(dataset: FinancialDataset, reconciliationId: string): ManagerialEntry[] {
  return dataset.entries.filter((entry) => entry.reconciliationId === reconciliationId);
}

export interface FinancialDrillDown {
  route: string;
  label: string;
  reference: string;
}

/**
 * Para onde navegar para ver o PEDIDO que originou a conciliação/margem (D-03
 * aplicado ao brief 3.8). `null` quando não há pedido vinculado.
 */
export function orderDrillDown(platformOrderId: string | null, orderExternalId?: string | null): FinancialDrillDown | null {
  if (!platformOrderId) return null;
  return {
    route: `/pedidos/${encodeURIComponent(platformOrderId)}`,
    label: "Abrir o pedido que originou o lançamento",
    reference: orderExternalId ?? platformOrderId
  };
}

/**
 * Para onde navegar para ver a CONCILIAÇÃO que originou o lançamento. `null`
 * quando o lançamento não nasceu de uma conciliação (ex.: despesa avulsa).
 */
export function reconciliationDrillDown(reconciliationId: string | null): FinancialDrillDown | null {
  if (!reconciliationId) return null;
  return {
    route: `/financeiro/conciliacao/${encodeURIComponent(reconciliationId)}`,
    label: "Abrir a conciliação que originou o lançamento",
    reference: reconciliationId
  };
}
